import { Layout } from '@/components/layout/Layout';
import { motion } from 'framer-motion';
import { Shield, Phone, MapPin } from 'lucide-react';

const PrivacyPolicyPage = () => {
  return (
    <Layout>
      <div className="container mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto"
        >
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-primary/10 rounded-full">
                <Shield className="h-8 w-8 text-primary" />
              </div>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-4">
              Privacy Policy â€“ <span className="text-gradient-rainbow">JoyAura</span>
            </h1>
            <p className="text-muted-foreground text-lg">
              Last updated on 3 January 2026
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none space-y-8">
            <div className="bg-card border rounded-lg p-6">
              <p className="text-muted-foreground mb-6">
                JoyAura ("we", "our", "us") operates the JoyAura mobile application,
                the website <strong>JoyAurafashion.com</strong>, and
                <strong> partner.JoyAurafashion.com</strong> (collectively, the â€œPlatformâ€).
              </p>

              <p className="text-muted-foreground mb-6">
                <strong>
                  JoyAura is owned and operated by Papisetti Subbaiah, a sole proprietorship
                  registered in India.
                </strong>
              </p>

              <p className="text-muted-foreground">
                JoyAura is a fashion marketplace where independent sellers list and sell
                products under the JoyAura ecosystem, including our in-house brand JoyAura.
                We are committed to protecting your privacy and ensuring transparency in how
                personal information is collected, used, and safeguarded.
              </p>
            </div>

            <section>
              <h2 className="text-2xl font-bold mb-4">1. Information We Collect</h2>
              <p className="mb-4">We may collect the following information:</p>
              <ul className="space-y-2 text-muted-foreground">
                <li>â€¢ Personal details such as name, phone number, and email address</li>
                <li>â€¢ Shipping and billing addresses</li>
                <li>â€¢ Order, return, and transaction details</li>
                <li>â€¢ Device and application usage information (Android devices)</li>
                <li>
                  â€¢ Payment-related information processed securely via authorized payment gateways
                  <br />
                  (We do not store card or banking details)
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">2. How We Use Your Information</h2>
              <p className="mb-4">We use your information to:</p>
              <ul className="space-y-2 text-muted-foreground">
                <li>â€¢ Process and deliver orders</li>
                <li>â€¢ Communicate order updates, refunds, and support responses</li>
                <li>â€¢ Improve platform performance and user experience</li>
                <li>â€¢ Detect and prevent fraud or misuse</li>
                <li>â€¢ Comply with applicable laws and regulatory requirements</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">3. Marketplace & Seller Information</h2>
              <p className="text-muted-foreground">
                JoyAura operates as a marketplace platform. Independent sellers list and sell
                products on JoyAura. Sellers may access limited customer information only for
                order fulfillment purposes and are contractually required to protect customer data.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">4. Data Sharing</h2>
              <p className="mb-4">We may share necessary information with:</p>
              <ul className="space-y-2 text-muted-foreground">
                <li>â€¢ Logistics and delivery partners</li>
                <li>â€¢ Payment gateway providers</li>
                <li>â€¢ Technology, analytics, and communication partners</li>
                <li>â€¢ Government or legal authorities when required by law</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                We do not sell or rent customer personal data to third parties.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">5. Data Security</h2>
              <p className="text-muted-foreground">
                We use reasonable technical and organizational measures to protect personal data.
                However, no electronic transmission or storage system is completely secure.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">6. Your Rights</h2>
              <p className="mb-4">You may:</p>
              <ul className="space-y-2 text-muted-foreground">
                <li>â€¢ Access or update your account information</li>
                <li>â€¢ Request deletion of your account (subject to legal requirements)</li>
                <li>â€¢ Opt out of non-essential communications</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">7. Changes to This Policy</h2>
              <p className="text-muted-foreground">
                JoyAura may update this Privacy Policy from time to time. Any changes will be
                posted on this page along with the updated date.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">8. Contact Information</h2>
              <div className="bg-secondary/50 rounded-lg p-6 space-y-4">
                <p className="font-semibold">For privacy-related concerns, contact:</p>
                <div className="space-y-3">
                  <p className="font-medium">JoyAura Support</p>

                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Phone className="h-4 w-4" />
                    <a
                      href="tel:+919345372307"
                      className="hover:text-primary transition-colors"
                    >
                      +91 9345372307
                    </a>
                  </div>

                  <div className="flex items-start gap-2 text-muted-foreground">
                    <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <p>1B, Anna Street, Ram Nagar, Ambattur OT,</p>
                      <p>Chennai, Tamil Nadu â€“ 600053</p>
                    </div>
                  </div>

                  <p className="text-sm text-muted-foreground">
                    Proprietor: <strong>Papisetti Subbaiah</strong>
                  </p>

                  <p className="text-sm text-muted-foreground">
                    Udyam Registration Number: UDYAM-TN-02-0426745
                  </p>
                </div>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
};

export default PrivacyPolicyPage;
